/**
 * `/demo/industry/:industry` — one industry and its demo teams (#2824).
 *
 * Page questions:
 *   1. Why does the visitor come? They picked an industry on `/demo` and want
 *      to know whether Tarii fits teams like theirs, and which example to try.
 *   2. What do they see first? The industry name, a short blurb and what such
 *      teams need. That is the one focal block. The team cards follow.
 *   3. What do they adjust? Only the choice of team. Each team card shows its
 *      size, hours and hard part, so the visitor can tell the teams apart and
 *      open the blurb page `/demo/:scenario`.
 *
 * An unknown industry, or one with no team while `showEmptyIndustries` is
 * off, shows a calm not-found state with a link back.
 */
import {
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  Clock,
  Gauge,
  Layers,
  type LucideIcon,
  MapPin,
  Moon,
  Scale,
  ShieldCheck,
  Users,
} from "lucide-react";
import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";

import { Logo } from "../components/Logo";
import { leaveDemoOnClick } from "../lib/demoSession";
import { DemoIndustryArt } from "./demo/DemoChapterArt";
import {
  type DemoRequirementIcon,
  findIndustry,
  showEmptyIndustries,
  teamFacts,
  teamsOf,
} from "./demo/demoIndustries";
import { displayTeamName, useDemoScenarios } from "./demo/demoScenarios";
import "./marketing/marketing-brand.css";

const NEED_ICONS: Record<DemoRequirementIcon, LucideIcon> = {
  moon: Moon,
  pin: MapPin,
  calendar: CalendarDays,
  badge: BadgeCheck,
  shield: ShieldCheck,
  users: Users,
  scale: Scale,
  layers: Layers,
  gauge: Gauge,
  clock: Clock,
};

export function DemoIndustryPage() {
  const { industry: industryId } = useParams<{ industry: string }>();
  const scenarios = useDemoScenarios();
  const industry = findIndustry(industryId);
  const teams = industry ? teamsOf(industry.id, scenarios.data ?? []) : [];
  const known = industry !== undefined && (showEmptyIndustries || teams.length > 0);
  const ready = !scenarios.isPending;

  return (
    <div
      data-marketing-brand=""
      className="dc-page-wash flex min-h-screen flex-col"
      data-testid="demo-industry-page"
    >
      <nav className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 pt-6 sm:px-6">
        <Link
          to="/demo"
          className="inline-flex min-h-tap items-center gap-1.5 text-sm underline-offset-4 hover:underline"
          data-testid="demo-industry-back"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back
        </Link>
        <a href="/" onClick={leaveDemoOnClick} aria-label="Back to the Tarii home page">
          <Logo className="h-8" />
        </a>
      </nav>
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-8 px-4 py-10 sm:px-6">
        {scenarios.isPending ? (
          <div className="min-h-[14rem]" aria-hidden="true" data-testid="demo-industry-loading" />
        ) : null}

        {scenarios.isError ? (
          <p className="text-sm" role="alert" data-testid="demo-industry-error">
            We could not load the demo teams. Try again in a moment.
          </p>
        ) : null}

        {ready && !scenarios.isError && !known ? (
          <section className="flex flex-col gap-3" data-testid="demo-industry-not-found">
            <h1 className="text-2xl font-semibold tracking-tight">We do not have that industry</h1>
            <p className="text-sm leading-relaxed">
              <Link to="/demo" className="underline underline-offset-2 hover:text-fg">
                See the demo industries
              </Link>
              .
            </p>
          </section>
        ) : null}

        {ready && known && industry ? (
          <>
            <header className="dc-hero" data-testid="demo-industry-hero">
              <div className="dc-head flex flex-col gap-4">
                <h1 data-testid="demo-industry-title">{industry.name}</h1>
                <p className="dc-hero-blurb" data-testid="demo-industry-blurb">
                  {industry.blurb}
                </p>
              </div>
              <div className="dc-hero-art" data-testid="demo-industry-hero-art">
                <DemoIndustryArt industryId={industry.id} fallback={0} />
              </div>
            </header>

            <section className="flex flex-col gap-4" aria-labelledby="demo-industry-needs">
              <h2 id="demo-industry-needs" className="dc-eyebrow">
                <span aria-hidden="true">1</span>
                What these teams need
              </h2>
              <div className="dc-needs" data-testid="demo-industry-requirements">
                {industry.requirements.map((item) => {
                  const Icon = NEED_ICONS[item.icon];
                  return (
                    <div key={item.label} className="dc-need" data-testid="demo-need">
                      <Icon className="dc-need-icon" aria-hidden="true" />
                      <p className="dc-need-label">{item.label}</p>
                      <p className="dc-need-text">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="flex flex-col gap-4" aria-labelledby="demo-industry-teams">
              <h2 id="demo-industry-teams" className="dc-eyebrow">
                <span aria-hidden="true">2</span>
                Pick a team
              </h2>
              {teams.length === 0 ? (
                <p className="text-sm" data-testid="demo-industry-empty">
                  Teams for this industry are on the way.
                </p>
              ) : (
                <ul
                  className="grid w-full list-none grid-cols-1 gap-5 p-0 md:grid-cols-2 lg:grid-cols-3"
                  data-testid="demo-team-cards"
                >
                  {teams.map((card, index) => {
                    const facts = teamFacts(card);
                    return (
                      <li key={card.name} className="dc-card">
                        <Link
                          to={`/demo/${card.name}`}
                          className="flex h-full flex-col no-underline outline-offset-[-3px]"
                          data-testid={`demo-card-${card.name}`}
                        >
                          <DemoIndustryArt
                            industryId={industry.id}
                            fallback={index === 0 ? 0 : 2}
                          />
                          <div className="flex flex-col gap-1 px-5 pb-5 pt-4">
                            <h3>{displayTeamName(card.team_name)}</h3>
                            <p className="dc-meta">{card.people_count} people</p>
                          </div>
                          <div className="dc-more">
                            <p className="dc-step" style={{ "--i": 0 } as CSSProperties}>
                              <b>Hours:</b>
                              {facts.hours}
                            </p>
                            <p className="dc-step" style={{ "--i": 1 } as CSSProperties}>
                              <b>The hard part:</b>
                              {facts.hardPart}
                            </p>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}
